import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import constantsServices from '../../../services/constants';
import { Creators as ReinforcementSubscribeActions } from '../../../../../domain/useCases/reinforcements/subscribeUseCase';
import { Creators as ReinforcementUnsubscribeActions } from '../../../../../domain/useCases/reinforcements/unsubscribeUseCase';
import { Creators as detailAction } from '../../../../../domain/useCases/reinforcements/getReinforcementDetailUseCase';
import ReinforcementsDetailView from './ReinforcementsDetailView';
import NotFoundViewController from '../../../../components/errors/NotFound/NotFoundViewController';
import NotAccesibleViewController from '../../../../components/errors/NotAccesible/NotAccesibleViewController';

import XCheckpointManager from '../../../../../core/managers/xcheckpointManager';
import { LocationState } from '../../constants';
import { useRole } from '@/presentation/hooks/useRole';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import useExternalSettings from '@/presentation/hooks/useExternalSettings';
import { Creators as registerWaitingListAction } from '@/domain/useCases/reinforcements/registerWaitingListUseCase';
import { Creators as unregisterWaitingListAction } from '@/domain/useCases/reinforcements/unregisterWaitingListUseCase';
import routes from '@/presentation/components/Routes/routingMap';

interface RouteParams {
  idReinforcement: string;
}

const ReinforcementsViewController = () => {
  const location = useLocation<LocationState>();
  const { isVisor } = useRole();
  const courseIdParam = location.state?.courseId ?? '';
  const { idReinforcement } = useParams<RouteParams>();
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [restriction, setRestriction] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showVacanciesModal, setShowVacanciesModal] = useState(false);
  const [showAccountNotRegistered, setShowAccountNotRegistered] =
    useState(false);
  const [showAccountNotActive, setShowAccountNotActive] = useState(false);
  const [loadReinforcementDetail, setLoadReinforcementDetail] = useState(false);
  const urlInitialReinforcement = `/${constantsServices?.servicesSections[1].url}`;
  const [registerWLError, setRegisterWLError] = useState(false);
  const history = useHistory();
  const { currentPeriod } = useExternalSettings();
  const dispatch = useDispatch();
  const goBackButton = () => {
    if (courseIdParam === 'byCalendar') {
      history.goBack();
    } else {
      history.push(urlInitialReinforcement, { courseId: courseIdParam });
    }
  };
  const { data, isLoading } = useSelector(
    (state: any) => state.reinforcementDetail
  );
  const {
    isLoading: isLoadingSubscribe,
    isSuccess: isSuccessRegister,
    error: errorRegister
  } = useSelector((state: any) => state.subscribeToReinforcement);
  const { isLoading: isLoadingUnsubscribe, isSuccess: isSuccessUnRegister } =
    useSelector((state: any) => state.unsubscribeToReinforcement);
  const stateWaitingList = useSelector(
    (state: any) => state.registerReinforcementWaitingList
  );

  const stateUnregisterWaitingList = useSelector(
    (state: any) => state.unregisterReinforcementWaitingList
  );

  const clearRegisterWaitingList = () => {
    setRegisterWLError(false);
    dispatch(registerWaitingListAction.registerReinforcementWaitingListClear());
  };

  const clearUnregisterWaitingList = () => {
    dispatch(
      unregisterWaitingListAction.unregisterReinforcementWaitingListClear()
    );
  };

  const clearRegisterWaitingListWhenError = () => {
    clearRegisterWaitingList();
    const router = routes.student.reinforcements.home;
    history.push(router);
  };

  const getEventDetail = () => {
    dispatch(detailAction.getReinforcementDetail(idReinforcement));
  };

  const validarNumero = (id: any) => {
    const regex = /^\d{1,10}$/;
    return regex.test(id);
  };

  useEffect(() => {
    GoogleAnalytics.writeLog(
      'portal-services-reinforcements-detail',
      'services',
      `Selected: ${(idReinforcement || '').toString()}`
    );
    return () => {
      clearRegisterWaitingList();
      clearUnregisterWaitingList();
    };
  }, []);
  useEffect(() => {
    if (validarNumero(idReinforcement)) {
      XCheckpointManager.registerStart('Refuerzo detalle');
      dispatch(detailAction.getReinforcementDetail(idReinforcement));
      setLoadReinforcementDetail(true);
    }
  }, [idReinforcement]);

  useEffect(() => {
    if (!isLoading && loadReinforcementDetail) {
      XCheckpointManager.registerEnd('Refuerzo detalle');
      setLoadReinforcementDetail(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isSuccessRegister || isSuccessUnRegister) {
      history.push(urlInitialReinforcement);
    }
  }, [isSuccessRegister, isSuccessUnRegister]);

  useEffect(() => {
    if (errorRegister === 'ERROR') setShowWarningModal(true);
    else if (errorRegister === 'VACANCIES') setShowVacanciesModal(true);
    else if (errorRegister === 'ZOOMACCOUNTNOTEXISTS')
      setShowAccountNotRegistered(true);
    else if (errorRegister === 'ZOOMACCOUNTDISABLE')
      setShowAccountNotActive(true);
  }, [errorRegister]);

  const handleUnsubscribe = () => {
    if (isVisor) {
      setRestriction(true);
      return;
    }
    dispatch(
      ReinforcementUnsubscribeActions.unsubscribeToReinforcement(data.code)
    );
  };
  const handleSubscribe = () => {
    if (isVisor) {
      setRestriction(true);
      return;
    }
    dispatch(ReinforcementSubscribeActions.subscribeToReinforcement(data.id));
  };
  useEffect(() => {
    if (isLoadingSubscribe && !isSuccessRegister) {
      XCheckpointManager.registerStart('Refuerzo reserva');
    }
    if (!isLoadingSubscribe && isSuccessRegister) {
      XCheckpointManager.registerEnd('Refuerzo reserva');
    }
  }, [isLoadingSubscribe, isSuccessRegister]);

  useEffect(() => {
    if (data?.statusWaitingList) {
      const statusMap: Record<string, string> = {
        LE: 'lista-espera-join',
        VD: 'lista-espera-join',
        HL: 'listaespera'
      };
      const status = statusMap[data.statusWaitingList];
      if (status) {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('status', status);
        history.replace({ search: searchParams.toString() });
      }
    } else {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.delete('status');
      history.replace({ search: searchParams.toString() });
    }
  }, [data, history]);

  const [
    showConfirmUnsuscribeWaitingList,
    setShowConfirmUnsuscribeWaitingList
  ] = useState(false);

  useEffect(() => {
    if (stateWaitingList.data?.status === 'Error') {
      setRegisterWLError(true);
      return;
    }
    if (stateWaitingList.isSuccess) {
      getEventDetail();
    }
  }, [stateWaitingList]);

  useEffect(() => {
    if (stateUnregisterWaitingList.isSuccess) {
      history.push(urlInitialReinforcement);
    }
  }, [stateUnregisterWaitingList]);

  if (
    (!isLoading && data && !data.isExist) ||
    !validarNumero(idReinforcement)
  ) {
    return <NotFoundViewController />;
  }
  if (!isLoading && data && !data.isAccessible) {
    return <NotAccesibleViewController />;
  }

  const registerWaitingListHandler = () => {
    if (isVisor) {
      setRestriction(true);
      return;
    }
    dispatch(
      registerWaitingListAction.registerReinforcementWaitingList({
        reinforcementId: idReinforcement,
        periodId: currentPeriod
      })
    );
    GoogleAnalytics.writeLog(
      'portal-services-reinforcements-waitingList',
      'services'
    );
  };

  const unregisterWaitingListHandler = () => {
    if (isVisor) {
      setRestriction(true);
      return;
    }
    dispatch(
      unregisterWaitingListAction.unregisterReinforcementWaitingList({
        reinforcementId: idReinforcement
      })
    );
    GoogleAnalytics.writeLog(
      'portal-services-reinforcements-waitingList-unregister',
      'services'
    );
  };

  return (
    <ReinforcementsDetailView
      goBackButton={goBackButton}
      isLoading={isLoading}
      event={data}
      handleSubscribe={handleSubscribe}
      showRegisterModal={showRegisterModal}
      setShowRegisterModal={setShowRegisterModal}
      showCancelModal={showCancelModal}
      setShowCancelModal={setShowCancelModal}
      onCancelSubscription={handleUnsubscribe}
      showWarningModal={showWarningModal}
      setShowWarningModal={setShowWarningModal}
      showVacanciesModal={showVacanciesModal}
      setShowVacanciesModal={setShowVacanciesModal}
      showAccountNotRegistered={showAccountNotRegistered}
      setShowAccountNotRegistered={setShowAccountNotRegistered}
      showAccountNotActive={showAccountNotActive}
      setShowAccountNotActive={setShowAccountNotActive}
      isProcessing={
        isLoadingSubscribe ||
        isLoadingUnsubscribe ||
        stateWaitingList.isLoading ||
        stateUnregisterWaitingList.isLoading
      }
      restriction={restriction}
      setRestriction={setRestriction}
      onRegisterWaitingList={registerWaitingListHandler}
      onUnregisterWaitingList={unregisterWaitingListHandler}
      // Waiting list modal props
      isRegisteredWaitingList={
        stateWaitingList.isSuccess && stateWaitingList.data?.status !== 'Error'
      }
      clearRegisterWaitingList={clearRegisterWaitingList}
      showWarningWaitingListModal={
        registerWLError || !!stateUnregisterWaitingList.error
      }
      errorWarningWaitingListMessage={
        stateWaitingList.data?.data ?? stateUnregisterWaitingList.error ?? ''
      }
      clearRegisterWaitingListWhenError={clearRegisterWaitingListWhenError}
      showConfirmUnsuscribeWaitingList={showConfirmUnsuscribeWaitingList}
      setShowConfirmUnsuscribeWaitingList={setShowConfirmUnsuscribeWaitingList}
    />
  );
};

export default ReinforcementsViewController;
